import type {
  NegativeKeyword,
  NegativeKeywordBulkIssue,
  NegativeKeywordBulkResult,
} from '../models/keyword.types'

// Pure decoders for the negative-keyword payloads, already out of their `{data}` envelope.
// A shape mismatch throws so the pane renders its retryable error state — falling back to `[]`
// is what made a broken read look like an empty workspace (ORP-22533).

function record(value: unknown, name: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`)
  }
  return value as Record<string, unknown>
}

function list(value: unknown, name: string): unknown[] {
  if (!Array.isArray(value)) throw new TypeError(`${name} must be an array`)
  return value
}

export function decodeKeywordRows(value: unknown): NegativeKeyword[] {
  return list(value, 'negative keywords').map((item, index) => {
    const row = record(item, `negative keywords[${index}]`)
    if (typeof row.keyword !== 'string') {
      throw new TypeError(`negative keywords[${index}].keyword must be a string`)
    }
    return row as NegativeKeyword
  })
}

function issues(value: unknown, name: string): NegativeKeywordBulkIssue[] {
  return list(value, name).map((item, index) => {
    const row = record(item, `${name}[${index}]`)
    return {
      ...(typeof row.index === 'number' ? { index: row.index } : {}),
      keyword: String(row.keyword ?? ''),
      reason: String(row.reason ?? ''),
    }
  })
}

export function decodeBulkResult(value: unknown): NegativeKeywordBulkResult {
  const body = record(value, 'bulk import result')
  if (typeof body.created !== 'number') {
    throw new TypeError('bulk import result.created must be a number')
  }
  return {
    created: body.created,
    skipped: issues(body.skipped, 'bulk import result.skipped'),
    errors: issues(body.errors, 'bulk import result.errors'),
  }
}
